import React, { useRef, useState, useEffect, Children, isValidElement, cloneElement } from 'react'
import { TabView, TabViewProps, TabPanel, TabViewTabChangeEvent, TabPanelProps } from 'primereact/tabview'

const getTabKey = (props: TabPanelProps, index: number): string => {
    return `${props.header?.toString()}-${index}`
}

const LazyTabView: React.FC<TabViewProps> = ({ children, onTabChange, activeIndex: externalActiveIndex, ...props }) => {
    const validChildren = Children.toArray(children).filter((child) => isValidElement(child) && child.type === TabPanel)

    const [internalActiveIndex, setInternalActiveIndex] = useState(0)
    const renderedTabs = useRef<Set<string>>(
        new Set([
            // Initialize with first tab if available
            validChildren.length > 0 && isValidElement<TabPanelProps>(validChildren[0])
                ? getTabKey(validChildren[0].props, 0)
                : '',
        ])
    )

    useEffect(() => {
        if (validChildren.length === 0) return

        const currentIndex = externalActiveIndex ?? internalActiveIndex
        const safeIndex = Math.min(currentIndex, validChildren.length - 1)

        if (safeIndex !== currentIndex && externalActiveIndex === undefined) {
            setInternalActiveIndex(safeIndex)
        }

        const child = validChildren[safeIndex]
        if (isValidElement<TabPanelProps>(child)) {
            renderedTabs.current.add(getTabKey(child.props, safeIndex))
        }
    }, [validChildren, externalActiveIndex, internalActiveIndex])

    const handleTabChange = (e: TabViewTabChangeEvent) => {
        const child = validChildren[e.index]
        if (isValidElement<TabPanelProps>(child)) {
            renderedTabs.current.add(getTabKey(child.props, e.index))
        }
        if (externalActiveIndex === undefined) {
            setInternalActiveIndex(e.index)
        }
        onTabChange?.(e)
    }

    const processedChildren = validChildren.map((child, index) => {
        if (isValidElement<TabPanelProps>(child)) {
            const tabKey = getTabKey(child.props, index)
            if (!renderedTabs.current.has(tabKey)) {
                return cloneElement<TabPanelProps>(child, { children: null })
            }
        }
        return child
    })

    return (
        <TabView
            {...props}
            activeIndex={externalActiveIndex ?? internalActiveIndex}
            onTabChange={handleTabChange}
            renderActiveOnly={false}
            scrollable
        >
            {processedChildren}
        </TabView>
    )
}

export { LazyTabView }
